import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import toastr from "toastr";
import EmbedController from "../../../../embed/EmbedController";
import AlertSlackOfError from "../../../../monitoring/AlertSlackOfError";
import { applyCustomLogoUpload, createUniqueShopifyProduct } from "../../../modules/createUniqueShopifyProduct";
import { getConfigurationBlocker } from "../../../modules/getConfigurationBlocker";
import { getFinalPrice } from "../../../modules/getFinalPrice";
import { getShoppingCartData } from "../../../modules/getShoppingCartData";
import { useProductScreenshotCapture } from "../../../modules/waitForProductScreenshot";
import { canvas_base64, session_id, update_items_activeIds } from "../../dataManagers/GlobalDataManagers";
import { AdditionalSection, FinalNotesAtom, QuantityAtom, RecipientNameAtom } from "./AdditionalSection.jsx";
import { TermsConditionsSection } from "./TermsConditionSection.jsx";
import "./styles.scss";

export const ShoppingCart = ({ cartTermsObj, productsState, componentsState, itemsState }) => {
  const setItemsActiveIds = useSetAtom(update_items_activeIds);
  const quantity = useAtomValue(QuantityAtom);
  const finalNotes = useAtomValue(FinalNotesAtom);
  const recipientName = useAtomValue(RecipientNameAtom);
  const [isCartSubmissionPending, setIsCartSubmissionPending] = useState(false);
  const [activeModalSection, setActiveModalSection] = useState("additionals");
  const [modalHeader, setModalHeader] = useState("TERMS AND CONDITIONS");
  const [cachedCartData, setCachedCartData] = useState(null);

  const custom_image_base64 = useAtomValue(canvas_base64);
  const waitForProductScreenshot = useProductScreenshotCapture();
  const sessionId = useAtomValue(session_id);
  // Always create the cart product at full MSRP. For dealers, Shopify B2B catalogs
  // apply the company discount on that price — putting the already-discounted total
  // here would double-discount. Builder UI still shows the discounted pay price.
  const isDealer = !!productsState?.activeObj?.shopify?.isDealer;
  const finalPrice = getFinalPrice(productsState, componentsState, itemsState, true);

  const handleCloseModal = useCallback(() => cartTermsObj.setState(false), [cartTermsObj]);

  const showCartError = useCallback(
    (errorMsg) => {
      console.log(`ERROR adding to shopping cart`);
      setIsCartSubmissionPending(false);
      setModalHeader("");
      setTimeout(() => setModalHeader("ERROR: please try again"), 500);
      alert("Error submitting to cart. Please try again.");
      AlertSlackOfError("ShoppingCart.jsx cart response", `error: ${errorMsg}`, sessionId);
    },
    [sessionId],
  );

  // setup callback for response when parent cart is updated
  useEffect(() => {
    if (EmbedController.isEmbedded) {
      EmbedController.setCartResponseCallback((errorMsg, redirectUrl) => {
        if (errorMsg || !redirectUrl) {
          showCartError(errorMsg);
        } else if (redirectUrl) {
          handleCloseModal();
          setIsCartSubmissionPending(false);
          EmbedController.sendParentUrlChange(redirectUrl);
        }
      });
    }
  }, [handleCloseModal, showCartError]);

  // if shopper has clicked "add to cart" and modal is showing for them to checkout
  useEffect(() => {
    if (cartTermsObj.state) {
      console.log("**************** initiate screenshot of product ****************");
      // Signal screenshot
      document.dispatchEvent(new CustomEvent("ScreenshotCanvasForCartImage"));
    }
  }, [cartTermsObj.state]); // Only depends on modal open state

  // Cache cart data whenever relevant data changes
  useEffect(() => {
    if (cartTermsObj.state) {
      console.log("**************** caching cart data ****************");
      // Debug logging for product data
      console.log("CART CACHE - Product data:", {
        productId: productsState?.activeObj?._id,
        brand: productsState?.activeObj?.shopify?.brand,
        model: productsState?.activeObj?.shopify?.model,
        year: productsState?.activeObj?.shopify?.year,
        displayName: productsState?.activeObj?.shopify?.displayName,
        handle: productsState?.activeObj?.shopify?.handle,
        url: window.location.href,
      });

      // Cache cart data
      const [cartData] = getShoppingCartData(productsState, componentsState, itemsState, quantity, finalNotes, recipientName);
      console.log("new cartData being set:", cartData);
      if (isDealer) {
        console.log(`[dealer] cart _msrp: ${cartData?.items?.[0]?.properties?._msrp}`);
      }
      setCachedCartData(cartData);
    }
  }, [cartTermsObj.state, productsState, componentsState, itemsState, quantity, finalNotes, recipientName, isDealer]); // Depends on all data that affects cart submission

  useEffect(() => {
    if (isCartSubmissionPending) setModalHeader("Enjoy Your Purchase!");
  }, [isCartSubmissionPending]);

  // Close modal when itemsState changes (which happens when URL hash changes)
  // this is an effort to fix the issue where product screenshot is correct config but the items state is not
  const prevItemsStateRef = useRef(null);
  useEffect(() => {
    // Only run after initial render when ref is set
    if (prevItemsStateRef.current && cartTermsObj.state) {
      // Convert to strings for deep comparison
      const currentItemsStr = JSON.stringify(itemsState);
      const prevItemsStr = JSON.stringify(prevItemsStateRef.current);

      // Close modal if there was an actual change to items
      if (currentItemsStr !== prevItemsStr) {
        handleCloseModal();
      }
    }

    // Update ref with current value for next comparison
    prevItemsStateRef.current = itemsState;
  }, [itemsState, cartTermsObj.state, handleCloseModal]);

  // Wrap submitToShoppingCart in useCallback that depends on cachedCartData
  const submitToShoppingCart = useCallback(async () => {
    setIsCartSubmissionPending(true);
    const cartSubmissionObj = cachedCartData;
    console.log("cartSubmissionObj", cartSubmissionObj);

    if (!cartSubmissionObj) {
      console.error("No cached cart data available");
      AlertSlackOfError("ShoppingCart.jsx submitToShoppingCart", "No cached cart data available", sessionId);
      setIsCartSubmissionPending(false);
      return;
    }

    // EMERGENCY HOTFIX: Prevent default product data from being submitted to cart
    const shopifyData = productsState?.activeObj?.shopify;
    const isDefaultData = !shopifyData?.brand && !shopifyData?.model && !shopifyData?.year;
    if (isDefaultData) {
      AlertSlackOfError(
        "Cart Bug - Default Product Data Detected",
        `Default product data being used in cart: ${JSON.stringify({
          brand: shopifyData?.brand,
          model: shopifyData?.model,
          year: shopifyData?.year,
          displayName: shopifyData?.displayName,
          handle: shopifyData?.handle,
          url: window.location.href,
        })}`,
      );
      setIsCartSubmissionPending(false);
      alert("Something went wrong when adding to the cart. Please refresh the page and try again - your configuration is saved.");
      return;
    }

    if (EmbedController.isEmbedded) {
      await applyCustomLogoUpload(cartSubmissionObj, sessionId, { itemsState, setItemsActiveIds });

      // set the _customUrl (must be done after a custom image has been set)
      cartSubmissionObj.items[0].properties._customUrl = EmbedController?.parentUrl || window.location.href;

      // Log the data being submitted for debugging
      console.log("Submitting cart data:", JSON.stringify(cartSubmissionObj));

      try {
        let productScreenshot = custom_image_base64;
        try {
          // Modal already requested the screenshot; wait for that capture instead of firing a second one
          productScreenshot = await waitForProductScreenshot({ capture: false });
        } catch (screenshotError) {
          AlertSlackOfError("ShoppingCart.jsx screenshot capture", `error: ${screenshotError}`, sessionId);
        }

        const created = await createUniqueShopifyProduct({
          productObj: productsState?.activeObj.shopify,
          cartSubmissionObj,
          finalPrice,
          customImageBase64: productScreenshot,
          sessionId,
        });

        cartSubmissionObj.items[0].id = created.variantId;

        console.log("Final cartSubmissionObj PROPERTIES: ", JSON.stringify(cartSubmissionObj.items[0].properties));
        EmbedController.sendAddToCart("/cart/add.js", cartSubmissionObj);
      } catch (error) {
        setIsCartSubmissionPending(false);
        setModalHeader("");
        setTimeout(() => setModalHeader("ERROR: please try again"), 500);
        alert("Error submitting to cart. Please try again.");
        AlertSlackOfError("ShoppingCart.jsx submitToShoppingCart error", `error: ${error}`, sessionId);
      }
    }
  }, [
    cachedCartData,
    setIsCartSubmissionPending,
    setModalHeader,
    setItemsActiveIds,
    itemsState,
    productsState,
    custom_image_base64,
    waitForProductScreenshot,
    sessionId,
    finalPrice,
  ]);

  const cartModal = () => (
    <Modal className="cart-modal" show={cartTermsObj.state} size="md" style={{ zIndex: 1202 }} onHide={handleCloseModal}>
      <button type="button" className="cart-modal-close" onClick={handleCloseModal} aria-label="Close">
        ×
      </button>
      {activeModalSection === "additionals" ? (
        <AdditionalSection
          activeModalSectionStateObj={{ state: activeModalSection, setState: setActiveModalSection }}
          handleCloseModal={handleCloseModal}
          componentsState={componentsState}
          itemsState={itemsState}
          quantity={quantity}
          productsState={productsState}
        />
      ) : (
        <TermsConditionsSection
          productsState={productsState}
          submitToShoppingCart={submitToShoppingCart}
          cardSubmissonPendingStateObj={{ state: isCartSubmissionPending, setState: setIsCartSubmissionPending }}
          activeModalSectionStateObj={{ state: activeModalSection, setState: setActiveModalSection }}
          modelHeaderStateObj={{ state: modalHeader, setState: setModalHeader }}
        />
      )}
    </Modal>
  );

  const configurationBlocker = getConfigurationBlocker(itemsState);
  if (configurationBlocker) {
    cartTermsObj.setState(false);
    toastr.error(configurationBlocker);
    return null;
  }

  return <div className="ShoppingCart">{cartModal()}</div>;
};
